/**
 * Shared pure-math LWPOLYLINE scene-geometry builder (DEFUSE D3 — clone
 * elimination).
 *
 * Takes the ALREADY-PARSED primitive inputs (raw pre-offset vertex `Vec3[]`,
 * the `closed` flag, the per-segment `bulges`, and the per-vertex
 * `widths`) and returns the `{ geometry, bounds }` pair that BOTH the legacy
 * `lwpolylineToRenderable.ts` (reads the `EntityPreview.properties` string bag)
 * and the typed `typed/lwpolylineFromEntity.ts` (reads the canonical
 * `LwpolylineEntity` fields) feed into `buildRenderableShell`.
 *
 * This is the single one-truth for the LWPOLYLINE render geometry: `applyOffset`
 * on every vertex, the optional-field omission rule (drop `bulges` / `widths`
 * when empty so the geometry shape matches what each path emitted inline
 * today), and the `boundsFromPoints` AABB (falling back to the origin point
 * when the polyline has no vertices, exactly as the legacy adapter did). Both
 * adapters differ ONLY in their value source (~few lines), so the math lives
 * here once and `dup:check` sees no typed↔legacy clone.
 *
 * Pure: no `EntityPreview` / `DrawingEntity` import, no side effects beyond
 * reading the locked-in coordinate origin via `applyOffset`.
 */

import {
  bulgeSegmentBounds,
  hasPolylineWidth,
  type PolylineWidth,
} from "@dxf-ai-editor/drawing-model";

import { applyOffset } from "../../coordinateOrigin";
import type { SceneGeometry, Vec3 } from "../../types";

import { boundsFromPoints } from "../adapterContext";

import { widePolylineBandBounds } from "./polylineWidthBand";

/** AABB shape carried on `RenderableEntity.bounds` (mirrors `boundsFromPoints`). */
export interface SceneBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** `{ geometry, bounds }` pair the builder returns. */
export interface LwpolylineSceneGeometryResult {
  geometry: SceneGeometry;
  bounds: SceneBounds;
}

// The per-vertex start/end width PAIR (DXF group codes 40 / 41) is
// `drawing-model`'s `polylineWidth.ts::PolylineWidth` — the same module that
// owns the rule for RESOLVING it out of an entity — re-exported here so this
// builder's existing callers keep their import path. One declaration, so a
// width pair cannot mean one thing to the adapters and another to the band.
export type { PolylineWidth };

/**
 * Build the `polyline` geometry + AABB from the raw (pre-offset) vertices, the
 * `closed` flag, the per-segment `bulges`, and the per-vertex `widths`.
 *
 * `applyOffset` is applied to EVERY vertex (the vertices are absolute
 * positions — identical to both siblings). `bulges` / `widths` are passed
 * through verbatim (already parsed by the caller) and are OMITTED from the
 * geometry when empty, so an arc-less / uniform-width polyline produces the
 * exact `{ kind, points, closed }` shape both paths emitted inline before.
 *
 * Bounds are the AABB of what is actually DRAWN. For a straight polyline that
 * is the vertex hull (`boundsFromPoints`). For a BULGED one it is the union of
 * the per-span arc boxes from `drawing-model`'s one-truth `bulgeSegmentBounds`,
 * because a bulged span bows OUTSIDE the chord/vertex hull and the batcher now
 * draws that bow (`batchedLayer.ts` (`polylineCentrelineSamples`), @codex PR #785
 * round 3 F1 — the bulge→arc half of OSNAP-INTERSECT-EMIT-S2, landed together
 * with the arc-aware OSNAP intersection shapes so snap and render never
 * disagree). Without the expansion the spatial-index prefilter box would stop
 * short of the drawn arc, and a window/crossing at the bow could drop a
 * VISIBLE entity — the under-report CLAUDE.md ПРИОРИТЕТ #1 forbids. (The
 * round-2 expansion was reverted in @codex round-3 #457 precisely BECAUSE the
 * renderer drew chords then; it draws arcs now, so the box follows it back.)
 * An empty polyline falls back to a single origin point `{0,0,0}` so the
 * spatial index still gets a (degenerate) AABB — exactly as the legacy adapter
 * did.
 */
export function buildLwpolylineSceneGeometry(
  verticesRaw: Vec3[],
  closed: boolean,
  bulges: number[],
  widths: PolylineWidth[],
): LwpolylineSceneGeometryResult {
  const points: Vec3[] = verticesRaw.map((p) => applyOffset(p));

  const base: SceneGeometry = {
    kind: "polyline",
    points,
    closed,
  };
  let geometry: SceneGeometry;
  if (bulges.length > 0) {
    geometry =
      widths.length > 0
        ? { ...base, bulges, widths }
        : { ...base, bulges };
  } else if (widths.length > 0) {
    geometry = { ...base, widths };
  } else {
    geometry = base;
  }

  const bounds = boundsFromPoints(
    points.length > 0 ? points : [{ x: 0, y: 0, z: 0 }],
  );

  return {
    geometry,
    bounds: expandBoundsForWidths(
      expandBoundsForBulges(bounds, points, closed, bulges),
      points,
      closed,
      bulges,
      widths,
    ),
  };
}

/**
 * UNION the box with the polyline's actual drawn BAND.
 *
 * Same invariant as {@link expandBoundsForBulges} one level up: the AABB is the
 * box of what is actually DRAWN, and `batchedLayer.ts` draws a width-carrying
 * polyline as its BAND (`polylineWidthOutline.ts`), never as its centreline. A
 * centreline-only box therefore stops short of the drawn edge, and the
 * spatial-index prefilter can then drop a VISIBLE entity from a
 * window/crossing at the band — the under-report CLAUDE.md ПРИОРИТЕТ #1
 * forbids.
 *
 * # Why a HALF-WIDTH PAD is not conservative (@codex PR #785 round 11, F4)
 *
 * This used to pad the centreline box uniformly by the largest half width,
 * which is only the band's reach along a STRAIGHT run. At a joint the two
 * offset edges INTERSECT, and `polylineWidthOutline.ts::miterJoint` accepts
 * that miter whenever `|t| <= MITER_LIMIT (4) · halfWidth` — so an accepted
 * outer corner can sit up to FOUR half-widths past the vertex, along the
 * bisector. The spike is not slack the renderer ignores: it is stroked. Width
 * 2 on `(0,0) → (10,0) → (1.34,5)`, a sharp join well inside the limit:
 *
 *     centreline hull ⊕ one half-width   maxX = 11
 *     generated band loop                maxX = 13.73195599951599   DRAWN
 *
 * — 2.73 units of visible geometry outside the box that claimed to contain it.
 *
 * So the expansion is taken from the GENERATED LOOPS
 * (`polylineWidthBand.ts::widePolylineBandBounds`, the AABB of the very call
 * the batcher and the vector plot stroke), which is EXACT rather than
 * conservative and needs no second copy of the miter rule here — writing one
 * would be the second truth this PR keeps closing. The result is still
 * UNION'd with the incoming box rather than replacing it: that box owns the
 * exact per-span `bulgeSegmentBounds` arc extrema (the band is a 64-per-turn
 * tessellation) and the empty-polyline fallback, and a bounds box must never
 * shrink.
 *
 * A width-less polyline returns the input box unchanged without building
 * anything — `hasPolylineWidth` is `drawing-model`'s own all-zero test, the
 * same predicate `cad-core`'s `lwpolylineDrawnBounds` gates on, and the band
 * builder would short-circuit such a list to the centreline anyway.
 *
 * The box is measured in the frame `points` are in. For a TILTED 2D heavy
 * POLYLINE that is the LIFTED frame, in which the band is not the drawn one —
 * `typed/polylineFromEntity.ts` therefore discards this whole box and rebuilds
 * it from the silhouette in the entity's own plane, exactly as before. That
 * one case consequently builds a band TWICE (3.7 us straight / 47 us bulged
 * per entity on this repo's bench), and it is accepted rather than hidden:
 * this builder is reached only for entities the LOD pass already resolved as
 * VISIBLE, the double is confined to width-carrying polylines with a
 * NON-DEFAULT extrusion, and removing it means either a flag parameter (a
 * caller telling a pure builder which of its own answers to skip) or hoisting
 * the tilt test above the builder — a restructure of that adapter, not of this
 * function.
 */
function expandBoundsForWidths(
  bounds: SceneBounds,
  points: Vec3[],
  closed: boolean,
  bulges: number[],
  widths: PolylineWidth[],
): SceneBounds {
  if (!hasPolylineWidth(widths)) return bounds;
  const band = widePolylineBandBounds(points, closed, bulges, widths);
  if (!band) return bounds;
  return {
    minX: Math.min(bounds.minX, band.minX),
    minY: Math.min(bounds.minY, band.minY),
    maxX: Math.max(bounds.maxX, band.maxX),
    maxY: Math.max(bounds.maxY, band.maxY),
  };
}

/**
 * Union the vertex-hull AABB with each BULGED span's swept-arc box.
 *
 * `bulgeSegmentBounds` (`drawing-model/bulgeArc.ts`) is the one truth for that
 * box — endpoints plus every cardinal extremum the sweep actually crosses — and
 * is the SAME function `viewportMembership.ts` uses for its conservative
 * model-space box, so the render prefilter and the viewport membership test can
 * never disagree about how far a bulged span reaches. A straight polyline
 * (`bulges` empty or all-zero) returns the input box unchanged, allocating
 * nothing.
 *
 * The box is measured in the frame `points` are in — the WCS XY plane for
 * every caller of this builder. For a TILTED 2D heavy POLYLINE the bow is NOT
 * in that plane (it is in the entity's own OCS plane), so this box is neither
 * the drawn extent nor a guaranteed superset of it; `typed/polylineFromEntity.ts`
 * therefore unions the REAL drawn samples
 * (`polylineWidthBand.ts::polylineCentrelineSamples`) on top for exactly those
 * entities. Over-reporting here is harmless — a prefilter box may over-report,
 * never under-report.
 */
function expandBoundsForBulges(
  bounds: SceneBounds,
  points: Vec3[],
  closed: boolean,
  bulges: number[],
): SceneBounds {
  if (bulges.length === 0 || points.length < 2) return bounds;
  let out = bounds;
  const spanCount = closed ? points.length : points.length - 1;
  for (let i = 0; i < spanCount; i += 1) {
    const bulge = bulges[i] ?? 0;
    if (bulge === 0) continue;
    const a = points[i];
    const b = points[(i + 1) % points.length];
    if (!a || !b) continue;
    const box = bulgeSegmentBounds(a, b, bulge);
    out = {
      minX: Math.min(out.minX, box.minX),
      minY: Math.min(out.minY, box.minY),
      maxX: Math.max(out.maxX, box.maxX),
      maxY: Math.max(out.maxY, box.maxY),
    };
  }
  return out;
}
